import { type To } from 'react-router';
import { Button } from '../Button/Button';
import { ButtonLink } from '../ButtonLink/ButtonLink';
import { Icon } from '../Icon/Icon';
import { type IconName } from '../Icon/icons';
import { IconTile } from '../IconTile/IconTile';
import { Tag } from '../Tag/Tag';
import { Text } from '../Text/Text';

type ActionBase = {
  label: string;
  leadingIcon?: IconName;
};

export type ActionCardAction =
  | (ActionBase & { onAction: () => void; to?: never; onClick?: never })
  | (ActionBase & {
      to: To;
      onAction?: never;
      onClick?: () => void;
    });

export type ActionCardProps = {
  icon: IconName;
  tag?: { label: string; icon?: IconName };
  title: string;
  description: string;
  action: ActionCardAction;
};

/**
 * A prominent card prompting the patient to take an action, e.g. adding
 * their insurance. The base of the dashboard action card pattern.
 */
export const ActionCard = ({
  icon,
  tag,
  title,
  description,
  action,
}: ActionCardProps) => {
  const leadingIcon = action.leadingIcon && (
    <Icon name={action.leadingIcon} size="large" />
  );

  return (
    <div className="p-m space-y-xs">
      <div className="flex items-start justify-between gap-xs">
        <IconTile icon={icon} intent="accent" size="large" />
        {tag && (
          <Tag
            intent="accent"
            size="small"
            leadingIcon={tag.icon && <Icon name={tag.icon} size="small" />}
          >
            {tag.label}
          </Tag>
        )}
      </div>
      <div className="space-y-xxxs">
        <Text as="h3" size="headingXS" color="contentDefault">
          {title}
        </Text>
        <Text size="bodyS" color="contentSubdued">
          {description}
        </Text>
      </div>
      {action.to !== undefined ? (
        <ButtonLink
          to={action.to}
          size="small"
          width="full"
          leadingIcon={leadingIcon}
          onClick={action.onClick}
        >
          {action.label}
        </ButtonLink>
      ) : (
        <Button
          size="small"
          width="full"
          leadingIcon={leadingIcon}
          onClick={action.onAction}
        >
          {action.label}
        </Button>
      )}
    </div>
  );
};
